import React, { useState, useRef, useEffect } from 'react'
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Slider,
} from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import { supabase } from '../../config/supabase'

interface LogoUploadProps {
  currentLogoUrl?: string
  onLogoChange: (url: string) => void
}

const CROP_WIDTH = 200
const CROP_HEIGHT = 140
const ASPECT_RATIO = CROP_WIDTH / CROP_HEIGHT

export default function LogoUpload({ currentLogoUrl, onLogoChange }: LogoUploadProps) {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [imageSrc, setImageSrc] = useState<string | null>(null)
  const [scale, setScale] = useState(1)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const imageRef = useRef<HTMLImageElement>(null)

  const handleOpenDialog = () => {
    setDialogOpen(true)
    setError(null)
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    setImageSrc(null)
    setSelectedFile(null)
    setScale(1)
    setError(null)
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      processFile(file)
    }
  }

  const handleFileDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    const file = event.dataTransfer.files?.[0]
    if (file) {
      processFile(file)
    }
  }

  const processFile = (file: File) => {
    if (!file.type.startsWith('image/')) {
      setError('Vælg venligst en billedfil')
      return
    }
    
    setSelectedFile(file)
    const reader = new FileReader()
    reader.onload = (e) => {
      setImageSrc(e.target?.result as string)
    }
    reader.readAsDataURL(file)
  }

  // Draw the cropped image on canvas whenever image or scale changes
  useEffect(() => {
    if (!imageSrc || !canvasRef.current || !imageRef.current) return

    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    const img = imageRef.current

    if (!ctx || !img.complete) return

    // Invert scale so higher values = zoom IN (smaller crop area)
    // Scale 1 = fit to image, scale 2 = zoom in 2x, scale 0.5 = zoom out 2x
    const invertedScale = 1 / scale

    // Calculate crop dimensions
    const imgAspect = img.naturalWidth / img.naturalHeight
    let cropWidth, cropHeight, cropX, cropY

    if (imgAspect > ASPECT_RATIO) {
      // Image is wider than crop ratio
      cropHeight = img.naturalHeight * invertedScale
      cropWidth = cropHeight * ASPECT_RATIO
      cropX = (img.naturalWidth - cropWidth) / 2
      cropY = (img.naturalHeight - cropHeight) / 2
    } else {
      // Image is taller than crop ratio
      cropWidth = img.naturalWidth * invertedScale
      cropHeight = cropWidth / ASPECT_RATIO
      cropX = (img.naturalWidth - cropWidth) / 2
      cropY = (img.naturalHeight - cropHeight) / 2
    }

    // Draw to canvas
    canvas.width = CROP_WIDTH
    canvas.height = CROP_HEIGHT
    ctx.drawImage(
      img,
      cropX,
      cropY,
      cropWidth,
      cropHeight,
      0,
      0,
      CROP_WIDTH,
      CROP_HEIGHT
    )
  }, [imageSrc, scale])

  const handleUpload = async () => {
    try {
      setUploading(true)
      setError(null)

      if (!canvasRef.current) {
        setError('Ingen billede at uploade')
        return
      }

      // Convert canvas to blob
      const blob = await new Promise<Blob>((resolve, reject) => {
        canvasRef.current!.toBlob((blob) => {
          if (blob) resolve(blob)
          else reject(new Error('Kunne ikke konvertere billede'))
        }, 'image/png')
      })

      const fileName = `${Date.now()}-${selectedFile?.name || 'logo.png'}`
      const filePath = `logos/${fileName}`

      const { error: uploadError } = await supabase.storage
        .from('undertaker-logos')
        .upload(filePath, blob, {
          contentType: 'image/png',
          upsert: false,
        })

      if (uploadError) {
        throw uploadError
      }

      // Get public URL
      const { data } = supabase.storage
        .from('undertaker-logos')
        .getPublicUrl(filePath)

      onLogoChange(data.publicUrl)
      handleCloseDialog()
    } catch (err: any) {
      console.error('Upload error:', err)
      setError(err.message || 'Upload fejlede')
    } finally {
      setUploading(false)
    }
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {currentLogoUrl && (
          <Box
            component="img"
            src={currentLogoUrl}
            alt="Current logo"
            sx={{
              width: 200,
              height: 140,
              objectFit: 'contain',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 2,
              bgcolor: 'background.paper',
            }}
          />
        )}
        <Button
          variant="contained"
          startIcon={<CloudUploadIcon />}
          onClick={handleOpenDialog}
        >
          {currentLogoUrl ? 'Skift Logo' : 'Upload Logo'}
        </Button>
      </Box>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Upload Logo
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Billedet vil blive beskåret til 200×140 pixels
          </Typography>
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {!imageSrc ? (
            <Box
              onDrop={handleFileDrop}
              onDragOver={(e) => e.preventDefault()}
              sx={{
                border: '2px dashed',
                borderColor: 'divider',
                borderRadius: 2,
                p: 6,
                textAlign: 'center',
                bgcolor: 'background.default',
                cursor: 'pointer',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'action.hover',
                },
              }}
            >
              <input
                accept="image/*"
                id="logo-file-input"
                type="file"
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />
              <label htmlFor="logo-file-input" style={{ cursor: 'pointer' }}>
                <CloudUploadIcon sx={{ fontSize: 64, color: 'action.active', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Træk og slip dit logo her
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  eller klik for at vælge en fil
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Understøtter: JPG, PNG, GIF
                </Typography>
              </label>
            </Box>
          ) : (
            <Box>
              {/* Hidden image for loading */}
              <img
                ref={imageRef}
                src={imageSrc}
                alt="Source"
                style={{ display: 'none' }}
                onLoad={() => setScale(1)}
              />

              {/* Preview canvas */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <canvas
                  ref={canvasRef}
                  style={{
                    maxWidth: '100%',
                    height: 'auto',
                    border: '2px solid #e0e0e0',
                    borderRadius: 8,
                  }}
                />
              </Box>

              {/* Zoom control */}
              <Box sx={{ px: 2 }}>
                <Typography variant="body2" gutterBottom>
                  Zoom
                </Typography>
                <Slider
                  value={scale}
                  onChange={(_, value) => setScale(value as number)}
                  min={0.5}
                  max={2}
                  step={0.01}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
                />
              </Box>

              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Button
                  variant="text"
                  onClick={() => {
                    setImageSrc(null)
                    setSelectedFile(null)
                    setScale(1)
                  }}
                >
                  Vælg et andet billede
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={uploading}>
            Annuller
          </Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            color="success"
            disabled={uploading || !imageSrc}
            startIcon={uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
          >
            {uploading ? 'Uploader...' : 'Gem Logo'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

